import moment from "moment-timezone";

export interface TimezoneOption {
  value: string;
  label: string;
  group: string;
  offsetMinutes: number;
  searchText: string;
}

const COMMON_GROUP = "Common";
const ALL_GROUP = "All time zones";

// Pinned to the top, in this order, to match the most common users.
const COMMON_TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
];

const CONTINENTS = new Set([
  "Africa",
  "America",
  "Antarctica",
  "Arctic",
  "Asia",
  "Atlantic",
  "Australia",
  "Europe",
  "Indian",
  "Pacific",
]);

// Extra search terms (cities, abbreviations) that aren't in the IANA name
const CITY_ALIASES: Record<string, string[]> = {
  "America/Los_Angeles": [
    "san diego",
    "san francisco",
    "san jose",
    "seattle",
    "las vegas",
    "portland",
    "sacramento",
    "california",
    "pacific time",
    "pst",
    "pdt",
    "pt",
  ],
  "America/Denver": [
    "salt lake city",
    "albuquerque",
    "mountain time",
    "colorado",
    "mst",
    "mdt",
    "mt",
  ],
  "America/Phoenix": ["arizona", "phoenix", "mst"],
  "America/Chicago": [
    "dallas",
    "houston",
    "austin",
    "san antonio",
    "minneapolis",
    "central time",
    "texas",
    "cst",
    "cdt",
    "ct",
  ],
  "America/New_York": [
    "nyc",
    "new york city",
    "boston",
    "miami",
    "atlanta",
    "philadelphia",
    "washington dc",
    "eastern time",
    "est",
    "edt",
    "et",
  ],
  "America/Toronto": ["toronto", "ottawa", "canada eastern"],
  "America/Anchorage": ["alaska", "akst", "akdt"],
  "Pacific/Honolulu": ["hawaii", "honolulu", "hst"],
  UTC: ["gmt", "utc", "universal", "zulu"],
  "Europe/London": ["london", "uk", "britain", "england", "gmt", "bst"],
  "Europe/Paris": ["paris", "france", "cet"],
  "Europe/Berlin": ["berlin", "germany", "cet"],
  "Europe/Madrid": ["madrid", "spain"],
  "Asia/Kolkata": ["india", "mumbai", "delhi", "bangalore", "ist"],
  "Asia/Dubai": ["dubai", "uae", "abu dhabi"],
  "Asia/Tokyo": ["tokyo", "japan", "jst"],
  "Asia/Singapore": ["singapore"],
  "Australia/Sydney": ["sydney", "melbourne", "australia", "aest"],
};

let cachedOptions: TimezoneOption[] | null = null;

export const getTimezoneOptions = (): TimezoneOption[] => {
  if (cachedOptions) return cachedOptions;

  const names = moment.tz.names().filter(_isDisplayableZone);
  const available = new Set(names);
  const common = COMMON_TIMEZONES.filter((zone) => available.has(zone));
  const commonSet = new Set(common);

  const restNames = names.filter((zone) => !commonSet.has(zone));
  restNames.push("UTC");

  const pinned = common.map((zone) => _buildOption(zone, COMMON_GROUP));
  const rest = restNames
    .map((zone) => _buildOption(zone, ALL_GROUP))
    .sort(
      (a, b) =>
        a.offsetMinutes - b.offsetMinutes || a.label.localeCompare(b.label),
    );

  cachedOptions = [...pinned, ...rest];
  return cachedOptions;
};

export const findTimezoneOption = (
  value?: string | null,
): TimezoneOption | null => {
  if (!value) return null;
  const options = getTimezoneOptions();
  const found = options.find((option) => option.value === value);
  if (found) return found;

  // Unknown/legacy stored value — synthesize an option so it still displays
  // and stays a valid Autocomplete option.
  const synthesized: TimezoneOption = {
    value,
    label: value,
    group: ALL_GROUP,
    offsetMinutes: moment.tz.zone(value) ? moment.tz(value).utcOffset() : 0,
    searchText: value.toLowerCase(),
  };
  options.push(synthesized);
  return synthesized;
};

export const filterTimezoneOptions = (
  options: TimezoneOption[],
  inputValue: string,
): TimezoneOption[] => {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  const terms = query.split(/\s+/);
  return options.filter((option) =>
    terms.every((term) => option.searchText.includes(term)),
  );
};

const _isDisplayableZone = (zone: string): boolean =>
  CONTINENTS.has(zone.split("/")[0]);

const _buildOption = (zone: string, group: string): TimezoneOption => {
  const offsetMinutes = moment.tz(zone).utcOffset();
  const label = _buildLabel(zone);
  const aliases = CITY_ALIASES[zone] ?? [];
  const searchText = [zone, label, ...aliases]
    .join(" ")
    .toLowerCase()
    .replace(/[_/]/g, " ");
  return { value: zone, label, group, offsetMinutes, searchText };
};

const _buildLabel = (zone: string): string => {
  if (zone === "UTC") return "UTC";
  const city = _cityName(zone);
  const abbr = moment.tz(zone).zoneAbbr();
  return abbr && abbr !== city ? `${city} (${abbr})` : city;
};

const _cityName = (zone: string): string => {
  const parts = zone.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
};
